import { useMe, useSocialStatus, useSystemSummary, useWorkerHealth } from '../../../api/queries';
import { Ic } from '../../../components/Ic';

function Row({ k, v, ok }: { k: string; v: string; ok?: boolean }) {
  return (
    <tr>
      <td>{k}</td>
      <td className="mono-sm">
        {ok !== undefined && (
          <span className={`pill ${ok ? 'green' : 'red'}`} style={{ marginRight: 8 }}>
            {ok ? 'set' : 'missing'}
          </span>
        )}
        {v}
      </td>
    </tr>
  );
}

/** Read-only on purpose.
 *
 *  A POST /api/settings/env would be new attack surface writing .env from an
 *  authenticated-but-not-necessarily-super-admin session, duplicating a flow
 *  /setup already covers behind an unlock token. */
export function EnvironmentTab() {
  const { data: me } = useMe();
  const { data: summary } = useSystemSummary();
  const { data: worker } = useWorkerHealth();
  const { data: social } = useSocialStatus();

  return (
    <>
      <div className="pcard">
        <div className="head">
          <Ic.settings className="ic" />
          <h3>Environment</h3>
          <span className="spacer" />
          <a className="btn sm" href="/setup">
            Reconfigure <Ic.external className="ico" />
          </a>
        </div>
        <div className="body pad">
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
            Values live in <code className="mono-sm">.env</code> on the host. Editing them happens in
            the setup wizard, which restarts the app afterwards.
          </div>
          <table className="assign">
            <tbody>
              <Row k="Build" v={summary?.commit || '—'} />
              <Row k="Webhook mode" v={summary?.webhook.mode ?? '—'} />
              <Row
                k="Worker"
                v={
                  worker?.alive
                    ? `alive · ${worker.jobs ?? '?'} jobs · heartbeat ${Math.round(worker.age_seconds ?? 0)}s ago`
                    : 'not responding'
                }
                ok={worker?.alive}
              />
              <Row k="Signed in as" v={`${me?.name ?? '—'} (${me?.role || 'unknown'})`} />
              <Row
                k="Dev mode"
                v={me?.dev_mode ? 'ON — auth is bypassed' : 'off'}
                ok={!me?.dev_mode}
              />
            </tbody>
          </table>
        </div>
      </div>

      <div className="pcard">
        <div className="head">
          <Ic.globe className="ic" />
          <h3>Social monitoring</h3>
        </div>
        <div className="body pad">
          <table className="assign">
            <tbody>
              <Row
                k="INSTAGRAM_ACCOUNTS"
                v={social?.instagram_account_ids.join(', ') || 'none configured'}
                ok={social?.instagram_configured}
              />
              <Row
                k="FACEBOOK_PAGE_IDS"
                v={social?.facebook_page_ids.join(', ') || 'none configured'}
                ok={social?.facebook_configured}
              />
            </tbody>
          </table>
        </div>
      </div>

      {me?.dev_mode && (
        <div className="pcard" style={{ borderColor: 'rgba(239,77,94,.35)' }}>
          <div className="body pad">
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <Ic.alert style={{ width: 17, height: 17, color: 'var(--err)', flexShrink: 0 }} />
              <div style={{ fontSize: 12.5, color: 'var(--fg-2)' }}>
                <strong>DISCORD_DEV_MODE is enabled.</strong> Every request runs as the super admin
                and Discord auth is skipped entirely. Turn this off before exposing the app.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
